import { AxiosError, isAxiosError } from "axios";
import { Properties, posthog } from "posthog-js";

import { uuidRegex } from "src/utils/uuidRegex";

const log: typeof console.log = (...args) => {
  // biome-ignore lint/suspicious/noConsole: this is logger
  console.log(...args);
};

const warn: typeof console.warn = (...args) => {
  // biome-ignore lint/suspicious/noConsole: this is logger
  console.warn(...args);
};

const error: typeof console.error = (...args) => {
  extractAndReportError(args);
  // biome-ignore lint/suspicious/noConsole: this is logger
  console.error(...args);
};

export const logger = {
  log,
  warn,
  error,
};

const extractAndReportError = (args: unknown[]): void => {
  if (!reportFirstError(args)) {
    try {
      // try stringifying
      reportError(new Error(JSON.stringify(args)));
    } catch {
      // try first element
      if (typeof args[0] === "string") {
        reportError(new Error(args[0]));
      } else {
        try {
          reportError(new Error(JSON.stringify(args[0])));
        } catch {
          reportError(new Error("Unknown error"));
        }
      }
    }
  }
};

const reportFirstError = (args: unknown[]): boolean => {
  const firstError = args.find((arg): arg is Error => arg instanceof Error);
  if (firstError) {
    reportError(firstError);
    return true;
  }
  return false;
};

const isIdSegment = (segment: string): boolean =>
  uuidRegex.test(segment) || /^\d+$/.test(segment);

const normalizeRequestPath = (
  url: string | undefined,
  baseUrl: string | undefined,
): string => {
  if (!url) {
    return "unknown";
  }
  try {
    const { pathname } = new URL(url, baseUrl || window.location.origin);
    return pathname
      .split("/")
      .map((segment) => (isIdSegment(segment) ? ":id" : segment))
      .join("/");
  } catch {
    return "unknown";
  }
};

// Request context for debugging HTTP failures. The grouping itself
// ($exception_fingerprint: page + status) is added in the before_send hooks
// in src/instrumentation/exceptionCapture.ts, so that autocaptured unhandled
// rejections get the same grouping as errors reported here.
const httpFailureProperties = (error: AxiosError): Properties => {
  const status = error.response?.status;
  return {
    ...(status !== undefined
      ? { http_status: status }
      : { http_error_code: error.code ?? "unknown" }),
    http_method: error.config?.method?.toUpperCase() ?? "unknown",
    http_path: normalizeRequestPath(error.config?.url, error.config?.baseURL),
  };
};

const exceptionProperties = (error: Error): Properties =>
  isAxiosError(error) ? httpFailureProperties(error) : {};

export const reportError = (error: Error): void => {
  try {
    // posthog is only initialized when keys exist and cookie consent allows it
    if (posthog.__loaded) {
      posthog.captureException(error, exceptionProperties(error));
    }
  } catch {
    // biome-ignore lint/suspicious/noConsole: this is logger
    console.warn("Could not log error to PostHog");
  }
  try {
    window.cwr?.("recordError", error);
  } catch {
    // biome-ignore lint/suspicious/noConsole: this is logger
    console.warn("Could not log error to AWS RUM");
  }
};
